/*
 * 统一前端日志工具：全链路排查核心
 *
 * - 所有业务代码统一通过 logger 输出日志，禁止散落 console.log；
 * - 每条日志自动携带 userSessionId / logId，运营可凭这两者串联
 *   后端 reco_api_log 与操作日志（reco_oper_log）完成全链路定位；
 * - 开发环境全量输出，生产环境仅输出 warn 及以上（避免刷屏与信息泄露）；
 * - error 级别可配置上报（上报失败静默，不影响业务）。
 *
 * @Author: Mustafa.Yin
 * @Date: 2026-10-31
 */
import { getStore } from '@/utils/store';
import { getUserSessionId } from '@/utils/sessionManager';

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };

const isProd = process.env.NODE_ENV === 'production';
// 生产只输出 warn 及以上；开发全量
const threshold = isProd ? LEVELS.warn : LEVELS.debug;

/** 需要上报后端的错误日志开关（上报复用 saveProblemRecord 通道时打开） */
let reportEnabled = false;

function currentSessionId() {
  try {
    return getUserSessionId() || '-';
  } catch (e) {
    return '-';
  }
}

function currentLogId() {
  try {
    const logId = getStore({ name: 'logId' });
    return logId || '-';
  } catch (e) {
    return '-';
  }
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function timeLabel() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

/**
 * 日志输出核心：格式 `[级别][时间][sid=会话][lid=日志] tag 数据`
 * 会话/日志标识取不到时显示 '-'，保证任何时点可安全调用。
 */
function output(level, tag, args) {
  if (LEVELS[level] < threshold) {
    return;
  }
  const prefix = `[${level.toUpperCase()}][${timeLabel()}][sid=${currentSessionId()}][lid=${currentLogId()}]`;
  const method = level === 'debug' ? 'log' : level;
  // eslint-disable-next-line no-console
  console[method].apply(console, [prefix, tag ? `[${tag}]` : '', ...args]);

  if (level === 'error' && reportEnabled && typeof window !== 'undefined') {
    reportError(tag, args);
  }
}

/**
 * 前端错误上报（fire-and-forget）：
 * 复用后端问题记录通道，把报错上下文带给后端，运营可在后台看到前端错误。
 * 上报失败静默处理，绝不影响主流程。
 */
function reportError(tag, args) {
  try {
    const message = args
      .map((a) => {
        if (a instanceof Error) return `${a.name}: ${a.message}`;
        if (typeof a === 'object') {
          try {
            return JSON.stringify(a);
          } catch (e) {
            return String(a);
          }
        }
        return String(a);
      })
      .join(' ');
    // problemType 后端已改为 String：'front_error'=前端错误上报（非 '0' 走系统问题邮箱通道），
    // 描述字段为 detail（后端无 problemDescribe 字段）
    const body = {
      problemType: 'front_error',
      detail: `[front][${tag || 'front'}] ${message}`.substring(0, 500),
    };
    const base = process.env.VUE_APP_BASE_API || '';
    window.fetch(`${base}smartreco/api/saveProblemRecord`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', userSessionId: currentSessionId() },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // 上报本身失败则静默
  }
}

export const logger = {
  debug: (tag, ...args) => output('debug', tag, args),
  info: (tag, ...args) => output('info', tag, args),
  warn: (tag, ...args) => output('warn', tag, args),
  error: (tag, ...args) => output('error', tag, args),
  /** 打开 error 级别自动上报后端（生产环境建议开启） */
  enableReport: () => {
    reportEnabled = true;
  },
};

export default logger;
